import { DataStore } from '@common/data-models/data-store/data-store';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { ViewStateFilter } from '@common/data-models/navigation/viewstate-filter';
import { ViewStateOptions } from '@common/data-models/navigation/viewstate-options';
import { ViewStateFilterSerializer, ViewStateOptionsSerializer } from '@common/data-models/serializers/serializers';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { DateTime } from '@suite/common/data-models/complex-types/date-time';

import { Record } from './record';
import { ViewStateRecord } from './viewstate-record';

export class PublicationViewStateRecord extends ViewStateRecord {
    public isViewStateCustomCardItemsBlank: boolean = false;

    // TODO
    public _publicationChannel: StorytellingPublicationChannelRecord = null;
    public get publicationChannel(): StorytellingPublicationChannelRecord {
        if (!this._publicationChannel || (this._publicationChannel && this._publicationChannel.Guid !== this.publicationChannelGUID && DataStore.publicationChannelsResults)) {
            // channel needs to be reloaded
            this._publicationChannel = DataStore.publicationChannelsResults.byGuid[this.publicationChannelGUID];
        }
        return this._publicationChannel;
    }
    public set publicationChannel(chan: StorytellingPublicationChannelRecord) {
        this._publicationChannel = chan;
    }

    private _publicationRevisionNumber: number = 0;
    public get publicationRevisionNumber(): number {
        return this._publicationRevisionNumber;
    }
    public set publicationRevisionNumber(value: number) {
        if (value !== this._publicationRevisionNumber) {
            const prev = this._publicationRevisionNumber;
            this._publicationRevisionNumber = value;
            if (this.PropertySubject) this.PropertySubject.next([PSTFieldNames.PSTPublicationListRevisionNumber, value, prev]);
        }
    }

    private _maxPublicationRevisionNumber: number = 0;
    public get maxPublicationRevisionNumber(): number {
        return this._maxPublicationRevisionNumber;
    }
    public set maxPublicationRevisionNumber(value: number) {
        this._maxPublicationRevisionNumber = value;
        this.publicationRevisionsList = new Array<{ id: number; title: string }>();
        for (let i = this._maxPublicationRevisionNumber; i >= 0; i--) {
            this.publicationRevisionsList.push({ id: i, title: (i + 1).toString() });
        }
    }
    public publicationRevisionsList: Array<{ id: number; title: string }> = [];

    private _publicationTime: DateTime = null;
    public get publicationTime(): DateTime {
        if (!this._publicationTime || !this._publicationTime.get()) {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            this._publicationTime = new DateTime();
            this._publicationTime.set(d);
        }
        return this._publicationTime;
    }
    public set publicationTime(value: DateTime) {
        let prev: DateTime = null;
        if (this._publicationTime !== null) {
            prev = this._publicationTime.deepCopy();
        }
        this._publicationTime = value;
        // since we are changing the time, we also need to reset the current rev and the max rev numbers
        // Check logic in order to populate publicationRevisionsList properly after rest of _maxPublicationRevisionNumber and _publicationRevisionNumber
        this._maxPublicationRevisionNumber = 0;
        this._publicationRevisionNumber = 0;
        this.publicationRevisionsList = new Array<{ id: number; title: string }>();
        for (let i = this._maxPublicationRevisionNumber; i >= 0; i--) {
            this.publicationRevisionsList.push({ id: i, title: (i + 1).toString() });
        }
        if (this.PropertySubject) this.PropertySubject.next([PSTFieldNames.PSTPublicationTime, value, prev]);
    }

    public initialize() {
        this.viewStateType = ViewStateTypes.PUBLICATION;
        super.initialize();
        this.viewStateLayout = ViewStateOptionsSerializer.Instance(Record.globalTablesService).initialize(this.viewStateType, PSTFieldNames.PSTViewStateLayout);
        this.viewStateFilter = ViewStateFilterSerializer.Instance(Record.globalTablesService).initialize(this.viewStateType);
    }

    public get publicationChannelGUID(): string {
        return this.getAs<string>('PSTPublicationChannelGUID', null);
    }
    public set publicationChannelGUID(value: string) {
        this.set('PSTPublicationChannelGUID', value);
    }

    public get publicationListTitle(): string {
        return this.getAs<string>(PSTFieldNames.PSTPublicationListTitle);
    }
    public set publicationListTitle(value: string) {
        this.set(PSTFieldNames.PSTPublicationListTitle, value);
    }

    public get editionName(): string {
        return this.getAs<string>(PSTFieldNames.PSTPublicationListEditionName);
    }
    public set editionName(value: string) {
        this.set(PSTFieldNames.PSTPublicationListEditionName, value);
    }

    public get viewStateGUID(): string {
        return this.getAs<string>(PSTFieldNames.PSTViewStateGUID, null);
    }
    public set viewStateGUID(value: string) {
        this.set(PSTFieldNames.PSTViewStateGUID, value);
    }

    public get viewStateLayout(): ViewStateOptions {
        return this.getAs<ViewStateOptions>('PSTViewStateLayout');
    }
    public set viewStateLayout(value: ViewStateOptions) {
        this.set('PSTViewStateLayout', value);
    }

    public get viewStateShowTopics(): boolean {
        return this.getAs<boolean>('PSTViewStateShowTopics', true);
    }
    public set viewStateShowTopics(value: boolean) {
        this.set('PSTViewStateShowTopics', value);
    }

    public get viewStateShowAssignments(): boolean {
        return this.getAs<boolean>('PSTViewStateShowAssignments', true);
    }
    public set viewStateShowAssignments(value: boolean) {
        this.set('PSTViewStateShowAssignments', value);
    }

    public get viewStateShowStories(): boolean {
        return this.getAs<boolean>('PSTViewStateShowStories', true);
    }
    public set viewStateShowStories(value: boolean) {
        this.set('PSTViewStateShowStories', value);
    }

    public get viewStateRemoveAddedRecords(): boolean {
        return this.getAs<boolean>('PSTViewStateRemoveAddedRecords', false);
    }
    public set viewStateRemoveAddedRecords(value: boolean) {
        this.set('PSTViewStateRemoveAddedRecords', value);
    }

    public get viewStateCustomCardItems(): Array<FieldDef> {
        return this.getAs<Array<FieldDef>>('PSTViewStateTopicCardCustomItems');
    }
    public set viewStateCustomCardItems(value: Array<FieldDef>) {
        this.set('PSTViewStateTopicCardCustomItems', value);
    }

    public get viewStateFilter(): ViewStateFilter {
        return this.getAs<ViewStateFilter>('PSTViewStateFilter');
    }
    public set viewStateFilter(value: ViewStateFilter) {
        this.set('PSTViewStateFilter', value);
    }

    public copyDataTo(target: Record, preserveGuid?: boolean, fieldsToSkip?: { [field: string]: boolean }, copyParentRef: boolean = false) {
        super.copyDataTo(target, preserveGuid, fieldsToSkip, copyParentRef);

        // copy publication view state, specifically publication time
        if (target instanceof PublicationViewStateRecord) {
            target.publicationTime = this.publicationTime.deepCopy();
        }
    }
}
